import React, { useState, useEffect } from 'react';
import { DeveloperData, AppData } from '../types';
import { StoreBadge } from '../components/StoreBadge';
import { AppIcon } from '../components/AppIcon';
import { DeveloperAvatar } from '../components/DeveloperAvatar';
import { appCatalog } from '../services/appCatalogService';
import { ArrowLeft, ArrowRight, Check, Star, Building2, Search, Loader2 } from 'lucide-react';

interface DevelopersViewProps {
  initialDevSlug?: string | null;
  onSelectApp: (appId: string) => void;
  onSelectDeveloper: (devSlug: string) => void;
}

export const DevelopersView: React.FC<DevelopersViewProps> = ({
  initialDevSlug,
  onSelectApp,
  onSelectDeveloper
}) => {
  const [selectedDevSlug, setSelectedDevSlug] = useState<string | null>(initialDevSlug || null);
  const [search, setSearch] = useState('');
  const [developersList, setDevelopersList] = useState<DeveloperData[]>([]);
  const [totalDevs, setTotalDevs] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedDevDetail, setSelectedDevDetail] = useState<(DeveloperData & { apps?: AppData[] }) | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  // Load developers list
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    const delayDebounce = setTimeout(() => {
      appCatalog.getDevelopers(search, 1, 60).then((res) => {
        if (isMounted) {
          setDevelopersList(res.results || []);
          setTotalDevs(res.total || 0);
          setIsLoading(false);
        }
      }).catch(() => {
        if (isMounted) setIsLoading(false);
      });
    }, 200);

    return () => {
      isMounted = false;
      clearTimeout(delayDebounce);
    };
  }, [search]);

  // Load single developer profile if selected
  useEffect(() => {
    if (!selectedDevSlug) {
      setSelectedDevDetail(null);
      return;
    }

    let isMounted = true;
    setIsLoadingDetail(true);
    appCatalog.getDeveloper(selectedDevSlug).then((dev) => {
      if (isMounted) {
        setSelectedDevDetail(dev);
        setIsLoadingDetail(false);
      }
    }).catch(() => {
      if (isMounted) setIsLoadingDetail(false);
    });

    return () => {
      isMounted = false;
    };
  }, [selectedDevSlug]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-8 animate-fadeIn">
      {selectedDevSlug ? (
        /* SINGLE DEVELOPER PROFILE */
        isLoadingDetail || !selectedDevDetail ? (
          <div className="py-24 flex flex-col items-center justify-center space-y-3">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <p className="text-xs text-slate-500 font-medium">Loading developer portfolio & analytics...</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Back button */}
            <button
              onClick={() => setSelectedDevSlug(null)}
              className="text-xs font-bold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 cursor-pointer"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
              <span>Back to all publishing studios</span>
            </button>

            {/* Dev Header Card */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-sm relative overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <DeveloperAvatar
                    avatarUrl={selectedDevDetail.avatarUrl}
                    name={selectedDevDetail.name}
                    size="xl"
                    shape="squircle"
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-3xl shadow-sm border border-slate-100 flex-shrink-0"
                  />
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h1 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
                        {selectedDevDetail.name}
                      </h1>
                      {selectedDevDetail.verified && (
                        <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-800 font-bold px-2 py-0.5 rounded-full">
                          <Check className="w-3 h-3" strokeWidth={2.5} />
                          <span>Verified Publisher</span>
                        </span>
                      )}
                    </div>
                    <div className="text-xs sm:text-sm text-slate-500 flex items-center gap-3">
                      <span>{selectedDevDetail.hqCountry || 'Global'}</span>
                      <span>•</span>
                      <span className="font-semibold text-slate-700">{selectedDevDetail.appsCount || selectedDevDetail.apps?.length || 1} published apps</span>
                    </div>
                  </div>
                </div>

                {/* Dev Score */}
                <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 text-center sm:text-right">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                    Studio Growth Score
                  </span>
                  <div className="text-3xl font-black text-blue-600 tracking-tight">
                    {selectedDevDetail.growthScore || 85} <small className="text-xs text-slate-400 font-normal">/ 100</small>
                  </div>
                  <span className="text-[11px] font-semibold text-emerald-600">Active App Store Publisher</span>
                </div>
              </div>

              <p className="text-xs sm:text-sm text-slate-600 mt-6 leading-relaxed max-w-3xl border-t border-slate-100 pt-4">
                {selectedDevDetail.about || `${selectedDevDetail.name} publishes mobile software for iOS and the Apple App Store.`}
              </p>
            </div>

            {/* Dev Metrics Strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Combined Installs</span>
                <strong className="text-2xl font-black text-slate-900">
                  {((selectedDevDetail.combinedEstimatedDownloads || 50000) / 1000000).toFixed(1)}M+
                </strong>
                <span className="text-xs text-slate-400 block mt-0.5">Estimated Monthly</span>
              </div>

              <div className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Combined Est. Revenue</span>
                <strong className="text-2xl font-black text-emerald-600">
                  ${((selectedDevDetail.combinedEstimatedRevenueUsd || 20000) / 1000000).toFixed(1)}M
                </strong>
                <span className="text-xs text-slate-400 block mt-0.5">Monthly model</span>
              </div>

              <div className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Portfolio Rating</span>
                <strong className="text-2xl font-black text-slate-900 inline-flex items-center gap-1">
                  <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                  <span>{(selectedDevDetail.averageRating || 4.5).toFixed(1)}</span>
                </strong>
                <span className="text-xs text-slate-400 block mt-0.5">Store average</span>
              </div>

              <div className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Categories</span>
                <strong className="text-sm font-bold text-slate-800 block truncate">
                  {selectedDevDetail.categories?.join(', ') || 'Utilities'}
                </strong>
                <span className="text-xs text-slate-400 block mt-0.5">Focus areas</span>
              </div>
            </div>

            {/* Published Apps Portfolio */}
            <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm space-y-4">
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">
                Discovered Applications ({selectedDevDetail.apps?.length || 0})
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(selectedDevDetail.apps || []).map((app) => (
                  <div
                    key={app.id}
                    onClick={() => onSelectApp(app.id)}
                    className="p-4 rounded-2xl border border-slate-200/80 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <AppIcon app={app} size="md" className="w-12 h-12 rounded-2xl shadow-2xs flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-bold text-slate-900 text-sm group-hover:text-blue-600 transition-colors truncate">
                            {app.name}
                          </h4>
                          <StoreBadge store={app.primaryStore} />
                        </div>
                        <span className="text-xs text-slate-400 block">{app.category} • Search Pos #{app.currentRank}</span>
                      </div>
                    </div>

                    <span className="text-xs font-bold text-blue-600 group-hover:underline flex-shrink-0 ml-2 inline-flex items-center gap-1">
                      <span>Inspect</span>
                      <ArrowRight className="w-3 h-3" strokeWidth={2} />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      ) : (
        /* DEVELOPERS DIRECTORY */
        <div className="space-y-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-slate-200/80">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold mb-2">
                <Building2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                <span>Publishing Studios Index</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-slate-950 tracking-tight">
                Developer Profiles & Portfolios
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Explore publishers, combined installs, portfolio ratings, and developer market presence across {totalDevs} indexed studios.
              </p>
            </div>

            <div className="w-full md:w-72">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search developers by name..."
                  className="w-full pl-9 pr-3 py-2 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 font-medium"
                />
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="py-20 flex flex-col items-center justify-center space-y-3">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              <p className="text-xs text-slate-500 font-medium">Loading developer profiles from catalog database...</p>
            </div>
          ) : developersList.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-200">
              <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <h3 className="font-bold text-slate-800">No Developers Found</h3>
              <p className="text-xs text-slate-500 mt-1">Try searching for a different studio name.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {developersList.map((dev) => (
                <div
                  key={dev.id}
                  onClick={() => setSelectedDevSlug(dev.slug)}
                  className="bg-white rounded-3xl p-6 border border-slate-200/80 hover:border-blue-300 hover:shadow-xl transition-all cursor-pointer group flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center gap-3.5 mb-4">
                      <DeveloperAvatar
                        avatarUrl={dev.avatarUrl}
                        name={dev.name}
                        size="md"
                        shape="squircle"
                        className="w-12 h-12 rounded-2xl shadow-sm group-hover:scale-105 transition-transform flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <h3 className="font-extrabold text-slate-900 text-base group-hover:text-blue-600 transition-colors truncate">
                          {dev.name}
                        </h3>
                        <span className="text-xs text-slate-400">{dev.hqCountry || 'Global'}</span>
                      </div>
                    </div>

                    <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed mb-4">
                      {dev.aiSummary || dev.about || `${dev.name} is an App Store developer.`}
                    </p>

                    <div className="grid grid-cols-2 gap-2 text-xs py-3 border-y border-slate-100 bg-slate-50/50 rounded-2xl px-3 mb-4">
                      <div>
                        <span className="text-slate-400 block text-[10px]">Estimated Installs</span>
                        <strong className="text-slate-800 font-bold">
                          {((dev.combinedEstimatedDownloads || 10000) / 1000000).toFixed(1)}M dl
                        </strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[10px]">Growth Score</span>
                        <strong className="text-blue-600 font-bold">{dev.growthScore || 80} / 100</strong>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs pt-2">
                    <span className="text-slate-500 font-medium">{dev.appsCount || 1} published apps</span>
                    <span className="font-bold text-blue-600 group-hover:underline inline-flex items-center gap-1">
                      <span>View Portfolio</span>
                      <ArrowRight className="w-3 h-3" strokeWidth={2} />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
